import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowBigLeft } from "lucide-react";
import AuthContext from "../context/AuthContext";
import { fetchOccurrencePreview } from "../api/OccurrenceEngagementAPI";
import { commentsStore, occurrenceTarget } from "../hooks/useCommentsStore";
import { occurrenceEngagementStore, useOccurrenceEngagement } from "../hooks/useOccurrenceEngagement";
import { formatEventStart } from "../utils/eventDomain";
import { useModal } from "./ModalManager";
import OccurrenceEngagementRow from "./OccurrenceEngagementRow";
import CommentsSection, { FULL_PERMISSIONS } from "./comments/CommentsSection";
import {
  engagementUnavailableCode,
  engagementUnavailableMessage,
  interactionErrorMessage,
  isAccessError,
  occurrenceCommentErrorMessages,
} from "./comments/commentErrors";

const DEFAULT_AVATAR = `${process.env.PUBLIC_URL}/logo512.png`;

// After a cancellation: nothing new, but the viewer can still delete their own
// comments and take back their own likes and reactions.
const CANCELED_PERMISSIONS = Object.freeze({
  canCreate: false,
  canInteract: false,
  canEditOwn: false,
  canDeleteOwn: true,
  canRemoveOwn: true,
});

// /occurrences/:id — the lightweight social view of one event occurrence,
// opened by every Home event-start card (whatever the viewer's RSVP) and by
// event engagement notifications. Everything comes from
// GET /event-occurrences/<id>/preview/, refetched on every open: who may see
// it, its engagement, and whether to offer "Show full detail"
// (can_view_full_detail — never inferred from the Home card).
//
//   (no query)     the occurrence, its engagement row, and its comments
//                  (?comments=1, from older links, is the same view)
//   ?comment=<id>  one comment's focused conversation
//
// Engagement lives in occurrenceEngagementStore (shared with Home's card);
// comments in the comments store under an occurrence target.
export default function OccurrencePreview() {
  const { token } = useContext(AuthContext);
  commentsStore.ensureSession(token);
  occurrenceEngagementStore.ensureSession(token);

  const { id: idParam } = useParams();
  const occurrenceId = Number(idParam);
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModal();
  const [searchParams] = useSearchParams();
  // ?comments=1 (older links) is the default view now: comments are always shown.
  const focusedCommentId = Number(searchParams.get("comment")) || null;

  const [preview, setPreview] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | unavailable | error
  const [errorMessage, setErrorMessage] = useState(null);
  const requestRef = useRef(0);
  const scrollContainerRef = useRef(null);

  const entry = useOccurrenceEngagement(occurrenceId);
  const engagement = entry && !entry.revoked ? entry.engagement : null;
  const target = useMemo(() => occurrenceTarget(occurrenceId), [occurrenceId]);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    const since = occurrenceEngagementStore.mark();
    try {
      const data = await fetchOccurrencePreview(occurrenceId);
      if (request !== requestRef.current) return;
      occurrenceEngagementStore.seed(occurrenceId, data.engagement, since);
      setPreview(data);
      setLoadState("ready");
    } catch (err) {
      if (request !== requestRef.current) return;
      if (err?.response?.status === 404) {
        // Out of the audience now (unfriended, blocked, made private, moved
        // into the future…): forget everything cached for it.
        occurrenceEngagementStore.revoke(occurrenceId);
        commentsStore.forgetTarget(occurrenceTarget(occurrenceId));
        setPreview(null);
        setLoadState("unavailable");
      } else {
        console.error("Failed to load event", err);
        setLoadState((current) => (current === "ready" ? current : "error"));
      }
    }
  }, [occurrenceId]);

  useEffect(() => {
    setPreview(null);
    setLoadState("loading");
    setErrorMessage(null);
    load();
  }, [load]);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [focusedCommentId]);

  // Any rejected engagement request may mean the occurrence changed (canceled,
  // moved, made private) or the viewer lost access: re-check it.
  const handleRequestError = useCallback(
    (err) => {
      if (isAccessError(err)) load();
    },
    [load]
  );

  // Rejected like/reaction: a fixed message by status or 409 code, then a
  // re-check of the occurrence.
  const reportEngagementError = (err, action) => {
    const code = engagementUnavailableCode(err);
    setErrorMessage(
      code
        ? engagementUnavailableMessage(code)
        : err?.response?.status === 404
          ? "This event is no longer available."
          : interactionErrorMessage(err, action)
    );
    handleRequestError(err);
  };

  const handleClose = () => {
    navigate(location.state?.backgroundLocation?.pathname || "/");
  };

  const pushView = (search) => {
    navigate(
      { pathname: location.pathname, search },
      { state: { ...location.state, viewDepth: (location.state?.viewDepth || 0) + 1 } }
    );
  };

  const goBackView = (fallbackSearch) => {
    if (location.state?.viewDepth > 0) navigate(-1);
    else navigate({ pathname: location.pathname, search: fallbackSearch }, { replace: true, state: location.state });
  };

  const openFullDetail = () => openModal("EventFocusModal", { occurrenceId });

  return (
    <div ref={scrollContainerRef} className="fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="h-full flex">
        <div className="hidden sm:block ml-9 sm:w-20 xl:w-56" />
        <div className="flex-1 flex justify-center">
          <div className="max-w-2xl w-full px-4 py-6 relative">
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="absolute top-7 -left-6 text-gray-600"
            >
              <ArrowBigLeft />
            </button>

            {loadState === "loading" && (
              <p className="py-10 text-center text-sm text-gray-400" role="status">
                Loading event…
              </p>
            )}
            {loadState === "error" && (
              <div className="py-10 text-center text-sm text-gray-500" role="alert">
                <p>Couldn't load this event.</p>
                <button type="button" onClick={load} className="mt-1 text-blue-500 hover:text-blue-600">
                  Retry
                </button>
              </div>
            )}
            {loadState === "unavailable" && (
              <div className="py-10 text-center text-sm text-gray-500" role="alert">
                <p>This event isn't available.</p>
                <button type="button" onClick={handleClose} className="mt-1 text-blue-500 hover:text-blue-600">
                  Close
                </button>
              </div>
            )}

            {loadState === "ready" && preview && (
              <>
                <OccurrenceHeader preview={preview} onClose={handleClose} />

                {/* As a post's focus view: the content and its actions, then
                    the comments; a focused conversation shows only itself. */}
                {!focusedCommentId && (
                  <>
                    <OccurrenceContent preview={preview} />

                    {preview.can_view_full_detail && (
                      <button
                        type="button"
                        onClick={openFullDetail}
                        className="mb-3 rounded-full border border-purple-200 bg-purple-50 px-4 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-100"
                      >
                        Show full detail
                      </button>
                    )}

                    {engagement ? (
                      <div className="mb-3 p-1">
                        <OccurrenceEngagementRow
                          occurrenceId={occurrenceId}
                          engagement={engagement}
                          onError={reportEngagementError}
                        />
                        {!engagement.can_engage && (
                          <p className="mt-1 text-xs text-gray-500">
                            {engagementUnavailableMessage("occurrence_canceled")}
                          </p>
                        )}
                        {errorMessage && (
                          <p className="mt-1 text-xs text-red-600" role="alert">
                            {errorMessage}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="py-4 text-sm text-gray-500">{engagementClosedMessage(preview)}</p>
                    )}
                  </>
                )}

                {engagement ? (
                  <CommentsSection
                    target={target}
                    permissions={engagement.can_engage ? FULL_PERMISSIONS : CANCELED_PERMISSIONS}
                    disabledMessage="This event was canceled. No new comments."
                    errorMessages={occurrenceCommentErrorMessages}
                    focusedCommentId={focusedCommentId}
                    onOpenConversation={(commentId) => pushView(`?comment=${commentId}`)}
                    onCloseConversation={() => goBackView("")}
                    onCommentCountChange={(count) =>
                      occurrenceEngagementStore.setCommentCount(occurrenceId, count)
                    }
                    onRequestError={handleRequestError}
                  />
                ) : (
                  focusedCommentId && (
                    <p className="py-6 text-center text-sm text-gray-500">
                      {engagementClosedMessage(preview)}
                    </p>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Why there's no engagement summary: not started yet, or private (the only
// two reasons the backend returns null).
function engagementClosedMessage(preview) {
  return new Date(preview.starts_at) > new Date()
    ? engagementUnavailableMessage("occurrence_not_started")
    : engagementUnavailableMessage("occurrence_private");
}

// The host, as a post's author row.
function OccurrenceHeader({ preview, onClose }) {
  const creator = preview.creator;
  return (
    <div className="flex items-center mb-3">
      <button type="button" onClick={onClose} aria-label="Back" className="flex pl-2 pr-2 sm:hidden text-gray-600">
        <ArrowBigLeft />
      </button>
      {creator && (
        <Link to={`/profiles/${creator.username}`} className="flex items-center gap-2 min-w-0">
          <img
            src={creator.profile_pic_url || DEFAULT_AVATAR}
            alt=""
            className="w-8 h-8 rounded-full object-cover border flex-shrink-0"
          />
          <span className="font-semibold truncate">{creator.username}</span>
        </Link>
      )}
    </div>
  );
}

// The event itself, where a post's media sits: title, start, canceled state.
function OccurrenceContent({ preview }) {
  const canceled = preview.status === "canceled";
  return (
    <section aria-label="Event" className="w-full border-t border-b py-4 px-1 mb-3">
      <h1 className={`text-xl font-semibold ${canceled ? "text-gray-500 line-through" : "text-gray-900"}`}>
        {preview.title}
      </h1>
      <p className="text-sm text-gray-600">
        {formatEventStart(preview.starts_at, preview.is_all_day, preview.timezone)}
      </p>
      {canceled && (
        <p className="mt-1 inline-block rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
          Canceled
        </p>
      )}
    </section>
  );
}
